import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { SkeletonRows } from "@/components/RegisterSkeleton";
import {
  Plus, Buildings, UsersThree, SignOut, X, Copy, CheckCircle,
  Prohibit, ArrowCounterClockwise, Trash,
} from "@phosphor-icons/react";

const inp = "w-full h-9 px-3 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none focus:border-equipr-black";
const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";

const STATUS_BADGE = {
  active: "bg-green-100 text-green-700",
  inactive: "bg-red-100 text-red-700",
};

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

function LinkModal({ link, emailSent, email, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  return (
    <div className="fixed inset-0 z-[60] bg-[rgba(26,26,26,0.34)] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-panel border border-border-warm rounded-[14px] overflow-hidden shadow-modal w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="tenant-link-modal">
        <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-divider-inner">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">New Organisation</div>
            <h3 className="font-bold text-[18px] text-ink-primary leading-tight mt-0.5">Organisation created</h3>
          </div>
          <button onClick={onClose} data-testid="tenant-link-modal-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-3">
          {emailSent ? (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2">
              <CheckCircle size={18} weight="fill" /> Invite email sent to <b>{email}</b>
            </div>
          ) : (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
              Email delivery isn't configured. Copy this link and send it to the organisation's admin so they can set their password.
            </div>
          )}
          <div>
            <label className={lbl}>Admin activation link (expires in 48h)</label>
            <div className="flex gap-2">
              <input readOnly value={link} className={inp + " text-xs bg-gray-50"} data-testid="tenant-link-input" onFocus={(e) => e.target.select()} />
              <button onClick={copy} data-testid="tenant-link-copy"
                className="h-9 px-3 bg-equipr-yellow text-equipr-black text-xs font-bold rounded-none hover:bg-equipr-yellow-dark flex items-center gap-1 whitespace-nowrap">
                <Copy size={14} weight="bold" /> {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const { data } = await api.post("/tenants", {
        name, admin_name: adminName, admin_email: adminEmail,
      });
      onCreated(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[55] bg-[rgba(26,26,26,0.34)] flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-surface-panel border border-border-warm rounded-[14px] overflow-hidden shadow-modal w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="tenant-create-modal">
        <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-divider-inner">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">Platform Admin</div>
            <h3 className="font-bold text-[18px] text-ink-primary leading-tight mt-0.5">New Organisation</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="tenant-create-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="tenant-create-error">{error}</div>}
          <div>
            <label className={lbl}>Organisation name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Acme Civil Pty Ltd"
              className={inp} data-testid="tenant-name-input" />
          </div>
          <div>
            <label className={lbl}>Admin full name</label>
            <input value={adminName} onChange={(e) => setAdminName(e.target.value)} required placeholder="Jane Smith"
              className={inp} data-testid="tenant-admin-name-input" />
          </div>
          <div>
            <label className={lbl}>Admin email</label>
            <input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} required placeholder="jane@acme.com.au"
              className={inp} data-testid="tenant-admin-email-input" />
            <p className="text-[11px] text-gray-500 mt-1">The admin receives an activation link to set their password.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-divider-inner bg-surface-panel">
          <button type="button" onClick={onClose} className="h-9 px-4 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[7px] hover:bg-surface-panel" data-testid="tenant-create-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="h-9 px-4 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60" data-testid="tenant-create-submit">
            {saving ? "Creating…" : "Create Organisation"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Tenants() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [linkInfo, setLinkInfo] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tenants");
      setTenants(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setTenants([]);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "super_admin") load();
  }, [user, load]);

  if (user === null) {
    return <div className="h-screen flex items-center justify-center bg-white"><div className="animate-pulse font-semibold" data-testid="tenants-loading">Loading…</div></div>;
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/" replace />;

  const doLogout = async () => { await logout(); navigate("/login"); };

  const toggleStatus = async (t) => {
    const next = t.status === "active" ? "inactive" : "active";
    await api.patch(`/tenants/${t.id}`, { status: next });
    load();
  };
  const removeTenant = async (t) => {
    if (!window.confirm(`Permanently delete "${t.name}" and ALL its data? This cannot be undone.`)) return;
    await api.delete(`/tenants/${t.id}`);
    load();
  };

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden bg-surface-page font-sans">
      <header className="h-14 bg-equipr-black text-white flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-4">
          <BrandLogo variant="dark" />
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-equipr-yellow border border-equipr-yellow/40 px-2 py-0.5">Platform Admin</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden sm:block text-xs text-gray-300" data-testid="tenants-current-user">{user?.name}</span>
          <button onClick={doLogout} data-testid="tenants-logout" className="text-gray-300 hover:text-equipr-yellow" title="Sign out"><SignOut size={18} weight="bold" /></button>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6" data-testid="tenants-page">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-extrabold text-equipr-black">Organisations</h1>
              <p className="text-sm text-gray-500">Provision and manage tenant organisations on the platform.</p>
            </div>
            <button onClick={() => setShowCreate(true)} data-testid="tenant-new-button"
              className="h-10 px-4 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px flex items-center gap-1.5">
              <Plus size={16} weight="bold" /> New Organisation
            </button>
          </div>

          {error && <div className="mb-4 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="tenants-error">{error}</div>}

          <div className="bg-surface-card border border-border rounded-[12px] shadow-card overflow-hidden">
            <table className="w-full text-sm" data-testid="tenants-table">
              <thead>
                <tr className="text-left text-colhead uppercase tracking-[0.1em] border-b border-border bg-surface-sunken">
                  <th className="px-4 py-3 font-bold text-ink-secondary">Organisation</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Status</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Users</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Projects</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Created</th>
                  <th className="px-4 py-3 text-right font-medium text-ink-muted">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants === null ? (
                  <SkeletonRows cols={6} rows={6} />
                ) : tenants.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-tertiary">No organisations yet.</td></tr>
                ) : tenants.map((t) => (
                  <tr key={t.id} className="border-b border-divider-row hover:bg-accent-wash" data-testid={`tenant-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 font-semibold text-ink-primary">
                        <Buildings size={16} weight="bold" className="text-ink-tertiary" /> {t.name}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-[4px] ${STATUS_BADGE[t.status] || "bg-surface-pressed text-ink-secondary"}`}>{t.status}</span>
                    </td>
                    <td className="px-4 py-3 text-ink-secondary"><span className="inline-flex items-center gap-1"><UsersThree size={14} /> {t.user_count ?? 0}</span></td>
                    <td className="px-4 py-3 text-ink-secondary">{t.project_count ?? 0}</td>
                    <td className="px-4 py-3 text-ink-tertiary">{fmt(t.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {t.status === "active" ? (
                          <button onClick={() => toggleStatus(t)} title="Deactivate" data-testid={`tenant-deactivate-${t.id}`}
                            className="p-1.5 text-ink-tertiary hover:text-red-600"><Prohibit size={16} weight="bold" /></button>
                        ) : (
                          <button onClick={() => toggleStatus(t)} title="Reactivate" data-testid={`tenant-reactivate-${t.id}`}
                            className="p-1.5 text-ink-tertiary hover:text-green-600"><ArrowCounterClockwise size={16} weight="bold" /></button>
                        )}
                        {t.id !== "default-tenant" && (
                          <button onClick={() => removeTenant(t)} title="Delete" data-testid={`tenant-delete-${t.id}`}
                            className="p-1.5 text-ink-tertiary hover:text-red-600"><Trash size={16} weight="bold" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={(data) => {
            setShowCreate(false);
            setLinkInfo({ link: data.invite_link, emailSent: data.email_sent, email: data.admin_email });
            load();
          }}
        />
      )}
      {linkInfo && <LinkModal {...linkInfo} onClose={() => setLinkInfo(null)} />}
    </div>
  );
}
